import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, List, Clock, BookOpen, Wrench, MessageCircle, CalendarCheck } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useBlog } from "@/lib/queries";
import { parseArticle, readingTime } from "@/lib/article";
import { articleSchema, breadcrumbSchema } from "@/lib/schema";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink } from "@/lib/contact";
import FinalCta from "@/sections/FinalCta";
import NotFound from "@/pages/NotFound";

function Inline({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-white">{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const { data: b, isLoading, isError } = useBlog(slug);
  const settings = useSettings();

  if (isError) return <NotFound />;
  if (isLoading || !b) return <div className="flex min-h-screen items-center justify-center text-neutral-500">Yükleniyor…</div>;

  const crumbs = [{ label: "Ana Sayfa", to: "/" }, { label: "Blog", to: "/blog" }, { label: b.title }];
  const { toc, blocks } = parseArticle(b.content);
  const mins = readingTime(b.content);

  return (
    <>
      <Seo
        title={b.seo_title || b.title}
        description={b.seo_description || b.excerpt}
        image={b.cover_image}
        canonicalPath={`/blog/${b.slug}`}
        jsonLd={[articleSchema({ title: b.title, description: b.seo_description || b.excerpt, image: b.cover_image, url: `/blog/${b.slug}`, datePublished: b.created_at }), breadcrumbSchema(crumbs)]}
      />
      <PageHero overline={b.category_name || "Araç Bakım Rehberi"} title={b.title} subtitle={b.excerpt} image={b.cover_image} breadcrumbs={crumbs}>
        <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-neutral-400">
          <span className="inline-flex items-center gap-1.5"><BookOpen className="h-4 w-4 text-gold" /> {b.category_name}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-gold" /> {mins} dk okuma</span>
        </div>
      </PageHero>

      <section className="obsidian py-16 sm:py-24">
        <div className="container-x grid gap-12 lg:grid-cols-[1.7fr_0.85fr]">
          <article>
            {toc.length >= 2 && (
              <Reveal>
                <nav data-testid="blog-toc" className="mb-10 border border-white/10 bg-white/[0.02] p-5 sm:p-6">
                  <span className="flex items-center gap-2 font-oswald text-[11px] uppercase tracking-[0.2em] text-gold"><List className="h-4 w-4" /> İçindekiler</span>
                  <ol className="mt-4 space-y-2">
                    {toc.map((t, i) => (
                      <li key={t.id}>
                        <a href={`#${t.id}`} data-testid={`toc-link-${t.id}`} className="group flex items-baseline gap-3 text-sm text-neutral-300 transition-colors hover:text-gold">
                          <span className="font-oswald text-xs text-gold/60">{String(i + 1).padStart(2, "0")}</span>
                          <span className="group-hover:underline underline-offset-4">{t.text}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              </Reveal>
            )}

            <div className="max-w-2xl">
              {blocks.map((blk, i) => {
                if (blk.type === "h2")
                  return <h2 key={i} id={blk.id} className="scroll-mt-28 mt-12 font-display text-2xl font-medium text-white sm:text-[1.7rem]">{blk.text}</h2>;
                if (blk.type === "h3")
                  return <h3 key={i} className="scroll-mt-28 mt-8 font-display text-xl font-medium text-white">{blk.text}</h3>;
                if (blk.type === "ul")
                  return (
                    <ul key={i} className="mt-5 space-y-2.5">
                      {blk.items.map((it, j) => (
                        <li key={j} className="flex gap-3 text-base leading-relaxed text-neutral-300">
                          <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-gold" /> <span><Inline text={it} /></span>
                        </li>
                      ))}
                    </ul>
                  );
                if (blk.type === "ol")
                  return (
                    <ol key={i} className="mt-5 space-y-3">
                      {blk.items.map((it, j) => (
                        <li key={j} className="flex gap-3.5 text-base leading-relaxed text-neutral-300">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-gold/40 font-oswald text-xs font-bold text-gold">{j + 1}</span>
                          <span className="pt-0.5"><Inline text={it} /></span>
                        </li>
                      ))}
                    </ol>
                  );
                return <p key={i} className="mt-5 text-base leading-relaxed text-neutral-300"><Inline text={blk.text} /></p>;
              })}

              <div className="mt-12 border border-gold/25 bg-gold/[0.04] p-6 sm:p-7">
                <h3 className="font-display text-xl font-medium text-white">Aracınız için profesyonel destek</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-300">En doğru koruma planını birlikte belirleyelim. Ücretsiz değerlendirme için bize ulaşın.</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/randevu" className="inline-flex items-center gap-2 bg-gold px-5 py-3 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300">
                    <CalendarCheck className="h-[18px] w-[18px]" /> Randevu Al
                  </Link>
                  <a href={whatsappLink(settings)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 font-oswald text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:border-gold/50">
                    <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </article>

          <aside className="space-y-6 lg:sticky lg:top-28 lg:h-fit">
            {b.related_services?.length > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-6">
                <h3 className="flex items-center gap-2 font-display text-lg font-medium text-white"><Wrench className="h-4 w-4 text-gold" /> İlgili hizmetler</h3>
                <ul className="mt-4 space-y-2.5">
                  {b.related_services.map((s) => (
                    <li key={s.slug}>
                      <Link to={`/hizmetler/${s.slug}`} data-testid={`blog-service-${s.slug}`} className="group flex items-center justify-between gap-2 border border-white/10 px-4 py-3 text-sm font-medium text-neutral-200 transition-colors hover:border-gold/40 hover:text-gold">
                        {s.name} <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {b.related_posts?.length > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-6">
                <h3 className="font-display text-lg font-medium text-white">İlgili yazılar</h3>
                <ul className="mt-4 space-y-3">
                  {b.related_posts.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/blog/${r.slug}`} data-testid={`blog-related-${r.slug}`} className="group flex items-center gap-3 border border-white/10 p-3 transition-colors hover:border-gold/40">
                        <img src={`${r.cover_image}?w=320`} alt={r.title} loading="lazy" decoding="async" className="h-14 w-14 shrink-0 object-cover grayscale-[0.2] transition group-hover:grayscale-0" />
                        <span className="text-sm font-medium leading-snug text-neutral-200 transition-colors group-hover:text-gold line-clamp-2">{r.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to="/blog" className="mt-4 inline-flex items-center gap-1.5 font-oswald text-xs uppercase tracking-[0.15em] text-gold">Tüm yazılar <ArrowUpRight className="h-3.5 w-3.5" /></Link>
              </div>
            )}
          </aside>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
